// Kansas State University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - K-State's Common Data Set 2025-2026 (Institutional Research): C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 (no early plans). Its G1
//    is marked as not final for 2026-2027 ("Check here if your institution's
//    2026-2027 academic year costs of attendance are not available"), so the
//    cost comes from Student Financial Assistance's 2026-2027 Undergraduate
//    Costs for the Manhattan campus.
//  - k-state.edu admissions: Detailed requirements for incoming freshmen
//    (updated 4/15/26), Step-by-step guide for incoming freshmen, and Dates
//    and deadlines for admission and scholarships (updated 7/23/26). The
//    deadlines page gives priority dates, not a final application deadline,
//    and K-State publishes no decision dates; the page shows what it states.
//  - College Scorecard API, school id 155399 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'K-State Common Data Set 2025-2026',
        url: 'https://www.k-state.edu/data/institutional-research/resources/common-data-set/CDS_2025_2026.pdf',
        read: '2026-10-01',
    },
    requirements: {
        label: 'K-State Admissions, Detailed requirements for incoming freshmen',
        url: 'https://www.k-state.edu/admissions/undergrad/manhattan/apply/incoming-freshmen/requirements.html',
        read: '2026-10-01',
    },
    guide: {
        label: 'K-State Admissions, Step-by-step guide for incoming freshmen',
        url: 'https://www.k-state.edu/admissions/undergrad/manhattan/apply/incoming-freshmen/guide.html',
        read: '2026-10-01',
    },
    dates: {
        label: 'K-State Admissions, Dates and deadlines for admission and scholarships',
        url: 'https://www.k-state.edu/admissions/undergrad/manhattan/apply/deadlines.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'K-State Student Financial Assistance, Undergraduate Costs 2026-2027',
        url: 'https://www.k-state.edu/sfa/cost/manhattan-campus-costs/undergraduate.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?155399-Kansas-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'kansas-state-university',
    kbId: 'kansas_state_university',
    name: 'Kansas State University',
    shortName: 'K-State',
    aliases: ['K-State', 'Kansas State'],
    location: 'Manhattan, KS',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (K-State brand colors page); decoration only.
    branding: {
        primary: '#512888',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/kansas_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 15591,
        admitted: 13642,
        enrolled: 3775,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 7088, admitted: 6643 },
            { group: 'Out-of-state', applied: 7102, admitted: 6536 },
            { group: 'International', applied: 1401, admitted: 463 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'K-State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'K-State has no early decision or early action plan. Its December 1 date is a priority date for admission and scholarships, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some programs are selective, with December 1 as their priority application date:',
        rows: [
            { school: 'College of Architecture, Planning & Design', requirement: 'Its programs, including interior design, are selective admission programs.' },
            { school: 'Carl R. Ice College of Engineering', requirement: 'Its programs are selective admission programs (the December 1 priority date is for domestic students).' },
            { school: 'Wildlife and outdoor enterprise management', requirement: 'A selective admission program in the College of Agriculture.' },
        ],
        source: 'dates',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority date', value: 'December 1' },
        rows: [
            { plan: 'Priority date (admission and general scholarships)', deadline: 'December 1', decision: null, binding: false },
        ],
        note: 'K-State keeps accepting applications after December 1, but scholarship offers after that date depend on unawarded funds. Competitive scholarship applications are due December 15, and the K-State Scholarship Network priority date is February 1. The FAFSA priority date is also December 1. K-State does not publish decision dates.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'K-State is test-optional. You are assured admission with a cumulative high school GPA of 3.25 or higher, or an ACT composite of 21 or SAT of 1060 or higher, plus a 2.0 GPA on any college courses taken in high school. If you miss those marks, you are still encouraged to apply and your application is reviewed individually. Some scholarships need an ACT or SAT, so K-State asks for scores by April 1 of your senior year.',
        source: 'requirements',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'guide',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and mandatory fees', amounts: [13132, 31446] },
            { item: 'Housing and food (on campus)', amounts: [13216, 13216] },
            { item: 'Books and supplies', amounts: [1052, 1052] },
            { item: 'Transportation', amounts: [1150, 1150] },
            { item: 'Personal costs', amounts: [2088, 2088] },
        ],
        totals: [30638, 48952],
        note: 'For a full-time undergraduate living on campus at 15 hours a semester; first-year students are required to live on campus. Housing is the median on-campus rate (options run $6,110 to $10,190) and meal plans run $4,790 to $5,610.',
        source: 'costs',
        netPrice: {
            average: 18210,
            byIncome: [
                { income: '$0 to $30,000', amount: 12650 },
                { income: '$30,001 to $48,000', amount: 11613 },
                { income: '$48,001 to $75,000', amount: 13499 },
                { income: '$75,001 to $110,000', amount: 19307 },
                { income: 'Over $110,000', amount: 23192 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from K-State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
